import { useCallback } from 'react';

import { useSelector } from '@xstate5/react';

import type { PermissionsMachineActorRef } from './permissions.machine';

// ─────────────────────────────────────────────────────────────────────────────

export const usePermissionsMachine = (actorRef: PermissionsMachineActorRef) => {
  //
  // ─── Flags ───────────────────────────────────────────────────────────

  const isLocationPermissionsLoading = useSelector(
    actorRef,
    (state) =>
      state.matches({ 'location-permission': 'waiting' }) ||
      state.matches({ 'location-permission': 'checking-if-enabled' }) ||
      state.matches({ 'location-permission': 'setup' }) ||
      state.matches({ 'location-permission': 'checking-if-suppressed' }),
  );

  const isLocationPrimerVisible = useSelector(actorRef, (state) =>
    state.matches({ 'location-permission': 'showing-primer' }),
  );

  const isRequestingLocationPermission = useSelector(actorRef, (state) =>
    state.matches({ 'location-permission': 'requesting-permission' }),
  );

  const isSkippingLocationPermission = useSelector(actorRef, (state) =>
    state.matches({ 'location-permission': 'skipping' }),
  );

  const isLocationPermissionApproved = useSelector(actorRef, (state) =>
    state.matches({ 'location-permission': 'approved' }),
  );

  const isLocationPermissionDenied = useSelector(actorRef, (state) =>
    state.matches({ 'location-permission': 'denied' }),
  );

  const isLocationPermissionSkipped = useSelector(actorRef, (state) =>
    state.matches({ 'location-permission': 'skipped' }),
  );

  const isLocationPermissionAlreadyShown = useSelector(actorRef, (state) =>
    state.matches({ 'location-permission': 'already-shown' }),
  );

  // ─── Handlers ────────────────────────────────────────────────────────

  const requestLocationPermissions = useCallback(() => {
    actorRef.send({ type: 'REQUEST_LOCATION_PERMISSIONS' });
  }, [actorRef]);

  const skipLocationPermissions = useCallback(() => {
    actorRef.send({ type: 'SKIP_LOCATION_PERMISSIONS' });
  }, [actorRef]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    isLocationPermissionsLoading,
    isLocationPrimerVisible,
    isRequestingLocationPermission,
    isSkippingLocationPermission,
    isLocationPermissionApproved,
    isLocationPermissionDenied,
    isLocationPermissionSkipped,
    isLocationPermissionAlreadyShown,
    requestLocationPermissions,
    skipLocationPermissions,
  };
};
